import { ComparisonSource } from 'ComponentLibrary/src/molecules/ComparisonChart/types'
import { Accordion, AccordionDetails, AccordionSummary, Link, Stack } from '@mui/material'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { Typography, TypographyWithSeparator } from 'ComponentLibrary/src/atoms/typography'
import { CaretDown, Files, Link as LinkIcon } from '@phosphor-icons/react'
import { TextWithEllipsis } from 'ComponentLibrary/src/molecules/TextWithEllipsis'
import { WarningTooltip } from 'ComponentLibrary/src/atoms/IconTooltip'
import { CounterDot } from 'ComponentLibrary/src/piazza/content/CounterDot/CounterDot'

interface ComparisonChartLegendProps {
    sources: ComparisonSource[]
    forceLegendOpen?: boolean
    copy: ComparisonChartLegendCopy
}

type ComparisonChartLegendCopy = {
    sourceDateWarning: string
    relevantDataPoints: (count: number) => string
    totalDataPoints: (count: number) => string
    unknownDataPointCount: string
    unnamedDataSeries: string
}

export const ComparisonChartLegend = ({ sources, forceLegendOpen, copy }: ComparisonChartLegendProps) => (
    <Stack direction="column" marginTop={tokens.SizingSpacing200}>
        {sources.map((source) => (
            <LegendEntry key={source.index} source={source} forceLegendOpen={forceLegendOpen} copy={copy} />
        ))}
    </Stack>
)

type LegendEntryProps = {
    source: ComparisonSource
    forceLegendOpen?: boolean
    copy: ComparisonChartLegendCopy
}

const LegendEntry = ({ source, forceLegendOpen, copy }: LegendEntryProps) => {
    const firstEntry = source.entries[0]
    const hasMultipleEntries = source.entries.length > 1
    const hasMultipleDescriptions = source.entries.some((entry) => entry.fullDescription !== firstEntry.fullDescription)
    const hasMultipleRegions = source.entries.some((entry) => entry.region !== firstEntry.region)
    const hasMultipleObservationTimes = source.entries.some((entry) => entry.observationTime !== firstEntry.observationTime)
    const hasMultipleRelevantDataCounts = source.entries.some(
        (entry) =>
            entry.relevantNumberOfEntries !== firstEntry.relevantNumberOfEntries ||
            entry.totalNumberOfEntries !== firstEntry.totalNumberOfEntries
    )
    const hasDifferingEntryValues =
        hasMultipleDescriptions || hasMultipleRegions || hasMultipleObservationTimes || hasMultipleRelevantDataCounts

    const hasDetails = source.url || source.notes || hasMultipleEntries || (!hasMultipleDescriptions && firstEntry.fullDescription)

    return (
        <Accordion
            key={source.title}
            disableGutters
            elevation={0}
            sx={{
                '&:before': { display: 'none' },
                '.MuiAccordionSummary-root': {
                    minHeight: 0,
                    padding: tokens.SizingSpacing050,
                },
            }}
            defaultExpanded={forceLegendOpen}
        >
            <AccordionSummary
                expandIcon={null}
                sx={{
                    '.MuiAccordionSummary-content': { flexGrow: 'unset', margin: tokens.SizingSpacingNone },
                    '.Mui-expanded #caret': { transform: 'rotate(180deg)' },
                    justifyContent: 'flex-start',
                    gap: tokens.SizingSpacing075,
                    pointerEvents: hasDetails ? 'initial' : 'none',
                }}
            >
                <Stack direction="column" gap={tokens.SizingSpacing025}>
                    <Stack direction="row" alignItems="baseline" columnGap={tokens.SizingSpacing050}>
                        <CounterDot number={source.index + 1} />
                        <Stack direction="row" alignItems="center" columnGap={tokens.SizingSpacing075} flexWrap="wrap">
                            <Typography variant="bodyTextMedium" color={tokens.ColorTextBoldest}>
                                {source.publisher}
                            </Typography>
                            <Typography variant="bodyTextMedium" color={tokens.ColorTextBold}>
                                {source.title}
                            </Typography>
                            {hasDetails && <CaretDown id="caret" size={tokens.SizingIconS} color={tokens.ColorIconBoldest} weight="bold" />}
                        </Stack>
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" columnGap={tokens.SizingSpacing050} paddingLeft={tokens.SizingSpacing300}>
                        {source.sourceDate && (
                            <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtle}>
                                {source.sourceDate}
                                {source.showSourceDateWarning && (
                                    <WarningTooltip style={{ margin: '-6px -8px -4px 0' }} text={copy.sourceDateWarning} />
                                )}
                            </TypographyWithSeparator>
                        )}
                        {!hasMultipleObservationTimes && source.observationTime && (
                            <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtle}>
                                {firstEntry.observationTime || source.observationTime}
                            </TypographyWithSeparator>
                        )}
                        {!hasMultipleRelevantDataCounts &&
                            (firstEntry.relevantNumberOfEntries ? (
                                <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtle}>
                                    {copy.relevantDataPoints(firstEntry.relevantNumberOfEntries)}
                                    {firstEntry.totalNumberOfEntries && ` (${copy.totalDataPoints(firstEntry.totalNumberOfEntries)})`}
                                </TypographyWithSeparator>
                            ) : (
                                <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtlest}>
                                    {copy.unknownDataPointCount}
                                </TypographyWithSeparator>
                            ))}
                        {!hasMultipleRegions && (
                            <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtle}>
                                {firstEntry.region || source.region}
                            </TypographyWithSeparator>
                        )}
                    </Stack>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingY: tokens.SizingSpacingNone, paddingX: tokens.SizingSpacing050 }}>
                <Stack
                    direction="column"
                    flexWrap="wrap"
                    gap={tokens.SizingSpacing050}
                    paddingLeft={tokens.SizingSpacing300}
                    paddingBottom={tokens.SizingSpacing100}
                >
                    {source.notes && (
                        <TextWithEllipsis
                            tooltipText={source.notes}
                            typographyProps={{
                                variant: 'bodyTextMedium',
                                color: tokens.ColorTextBold,
                                style: { maxWidth: '100%' },
                            }}
                        >
                            {source.notes}
                        </TextWithEllipsis>
                    )}
                    {!hasMultipleDescriptions && firstEntry.fullDescription && (
                        <TextWithEllipsis
                            tooltipText={firstEntry.fullDescription}
                            typographyProps={{
                                variant: 'bodyTextMedium',
                                color: tokens.ColorTextBold,
                                style: { maxWidth: '100%' },
                            }}
                        >
                            {firstEntry.fullDescription}
                        </TextWithEllipsis>
                    )}
                    {source.url && source.urlName && (
                        <Stack direction="row" width="100%" gap={tokens.SizingSpacing075} alignItems="center">
                            {source.sourceType === 'web' ? (
                                <LinkIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorTextSubtle} />
                            ) : (
                                <Files size={tokens.SizingIconS} weight="bold" color={tokens.ColorTextSubtle} />
                            )}
                            <Link sx={{ width: 'calc(100% - 24px)' }} href={source.url} color={tokens.ColorIconLinkDefault} target="_blank">
                                <TextWithEllipsis
                                    tooltipText={source.urlName}
                                    typographyProps={{
                                        color: tokens.ColorTextLinkDefault,
                                        variant: 'bodyTextMedium',
                                    }}
                                >
                                    {source.urlName}
                                </TextWithEllipsis>
                            </Link>
                        </Stack>
                    )}
                    {hasMultipleEntries &&
                        source.entries.map((entry, entryIndex) => (
                            <Stack key={entry.index} direction="column" paddingTop={tokens.SizingSpacing050} flexWrap="wrap">
                                <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                    <CounterDot
                                        variant="secondary"
                                        number={source.index + 1}
                                        overwrite={String.fromCharCode(65 + entryIndex)}
                                    />
                                    <Typography variant="bodyTextMedium" color={tokens.ColorTextBold}>
                                        {entry.shortDescription || copy.unnamedDataSeries}
                                    </Typography>
                                </Stack>
                                {hasDifferingEntryValues && (
                                    <Stack direction="row" paddingTop={tokens.SizingSpacing050} paddingLeft={tokens.SizingSpacing300}>
                                        {hasMultipleObservationTimes && (
                                            <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtle}>
                                                {entry.observationTime ?? source.observationTime}
                                            </TypographyWithSeparator>
                                        )}
                                        {hasMultipleRelevantDataCounts &&
                                            (entry.relevantNumberOfEntries ? (
                                                <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtle}>
                                                    {copy.relevantDataPoints(entry.relevantNumberOfEntries)}
                                                    {entry.totalNumberOfEntries && ` (${copy.totalDataPoints(entry.totalNumberOfEntries)})`}
                                                </TypographyWithSeparator>
                                            ) : (
                                                <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtlest}>
                                                    {copy.unknownDataPointCount}
                                                </TypographyWithSeparator>
                                            ))}
                                        {hasMultipleRegions && (
                                            <TypographyWithSeparator variant="bodyTextMedium" color={tokens.ColorTextSubtle}>
                                                {entry.region ?? source.region}
                                            </TypographyWithSeparator>
                                        )}
                                    </Stack>
                                )}
                                {hasMultipleDescriptions && entry.fullDescription && (
                                    <Typography
                                        variant="bodyTextMedium"
                                        color={tokens.ColorTextBold}
                                        style={{ paddingLeft: tokens.SizingSpacing300 }}
                                    >
                                        {entry.fullDescription}
                                    </Typography>
                                )}
                            </Stack>
                        ))}
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}
